import React from 'react';
import { Card, Timeline, Progress, Tag } from 'antd';
import { ClockCircleOutlined } from '@ant-design/icons';

interface TimelineData {
    task_created: number;
    redis_query_end: number;
    stats_calculation_end?: number;
    strategy_decision_end?: number;
    matrix_fill_start?: number;
    matrix_fill_end?: number;
    matrix_reorder_start?: number;
    matrix_reorder_end?: number;
    partition_identify_start?: number;
    partition_identify_end?: number;
    query_blocks_build_start?: number;
    query_blocks_build_end?: number;
    osrm_calls_start: number;
    osrm_calls_end: number;
    redis_write_start: number;
    redis_write_end: number;
    matrix_restore_start?: number;
    matrix_restore_end?: number;
    task_completed: number;
    osrm_calls?: Array<{
        block_id: number;
        start: number;
        duration: number;
        end: number;
    }>;
}

interface ExecutionTimelineProps {
    timeline: TimelineData;
    durationMs: number;
}

const ExecutionTimeline: React.FC<ExecutionTimelineProps> = ({ timeline, durationMs }) => {
    const phases = [
        {
            name: '任务创建',
            start: timeline.task_created,
            end: timeline.redis_query_end,
            color: 'blue'
        },
        {
            name: 'Redis批量查询',
            start: timeline.task_created,
            end: timeline.redis_query_end,
            color: 'cyan'
        },
        {
            name: '统计命中率',
            start: timeline.redis_query_end,
            end: timeline.stats_calculation_end || timeline.strategy_decision_end || timeline.redis_query_end,
            color: 'geekblue',
            optional: true
        },
        {
            name: '矩阵重排',
            start: timeline.matrix_reorder_start || 0,
            end: timeline.matrix_reorder_end || 0,
            color: 'purple',
            optional: true
        },
        {
            name: '分区识别 + 查询块构建',
            start: timeline.partition_identify_start || timeline.query_blocks_build_start || 0,
            end: timeline.query_blocks_build_end || 0,
            color: 'magenta',
            optional: true
        },
        {
            name: 'OSRM API调用',
            start: timeline.osrm_calls_start,
            end: timeline.osrm_calls_end,
            color: 'volcano'
        },
        {
            name: 'Redis批量写入',
            start: timeline.redis_write_start,
            end: timeline.redis_write_end,
            color: 'orange'
        },
        {
            name: '完成并返回',
            start: timeline.redis_write_end,
            end: timeline.task_completed,
            color: 'green'
        }
    ].filter(phase => !phase.optional || (phase.end > phase.start));

    const getPhasePercent = (start: number, end: number) => {
        return ((end - start) / durationMs) * 100;
    };

    const maxPhase = phases.reduce((max, phase) => {
        const duration = phase.end - phase.start;
        return duration > (max.end - max.start) ? phase : max;
    }, phases[0]);

    return (
        <Card title="⏱️ 执行时间线">
            <div className="space-y-6">
                {/* 可视化时间线条 */}
                <div className="space-y-3">
                    {phases.map((phase, index) => {
                        const duration = phase.end - phase.start;
                        const percent = getPhasePercent(phase.start, phase.end);

                        return (
                            <div key={index}>
                                <div className="flex justify-between items-center mb-1">
                                    <span className="text-sm font-medium">{phase.name}</span>
                                    <span className="text-xs text-gray-500">
                                        {phase.start}ms - {phase.end}ms ({duration}ms)
                                    </span>
                                </div>
                                <Progress
                                    percent={percent}
                                    strokeColor={
                                        phase.name === maxPhase.name ? '#ff4d4f' : undefined
                                    }
                                    showInfo={false}
                                />
                            </div>
                        );
                    })}
                </div>

                {/* OSRM调用详情 */}
                {timeline.osrm_calls && timeline.osrm_calls.length > 0 && (
                    <div className="border-t pt-4">
                        <h4 className="text-sm font-semibold mb-3">
                            🔍 OSRM API调用详情 ({timeline.osrm_calls.length}次)
                        </h4>
                        <div className="space-y-2">
                            {timeline.osrm_calls.map((call) => (
                                <div
                                    key={call.block_id}
                                    className="flex items-center gap-3 text-sm bg-gray-50 p-2 rounded"
                                >
                                    <Tag color="blue">块 #{call.block_id}</Tag>
                                    <span className="text-gray-600">
                                        {call.start}ms - {call.end}ms
                                    </span>
                                    <span className="font-semibold text-orange-600">
                                        {call.duration}ms
                                    </span>
                                    <Progress
                                        percent={getPhasePercent(call.start, call.end)}
                                        size="small"
                                        showInfo={false}
                                        className="flex-1"
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* 最耗时阶段提示 */}
                <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
                    <div className="flex items-center gap-2">
                        <ClockCircleOutlined className="text-yellow-600" />
                        <span className="font-semibold">最耗时阶段: </span>
                        <span>{maxPhase.name}</span>
                        <Tag color="orange">
                            {maxPhase.end - maxPhase.start}ms
                        </Tag>
                        <span className="text-gray-600">
                            ({((maxPhase.end - maxPhase.start) / durationMs * 100).toFixed(1)}%)
                        </span>
                    </div>
                </div>

                {/* 详细时间线 */}
                <div className="border-t pt-4">
                    <Timeline
                        items={phases.map((phase) => ({
                            color: phase.color,
                            children: (
                                <div>
                                    <div className="font-medium">{phase.name}</div>
                                    <div className="text-xs text-gray-500">
                                        {phase.start}ms → {phase.end}ms ({phase.end - phase.start}ms)
                                    </div>
                                </div>
                            )
                        }))}
                    />
                </div>
            </div>
        </Card>
    );
};

export default ExecutionTimeline;
